/*
 * Account sagas
 */

import * as Sentry from '@sentry/browser';
import { all, call, put, select } from 'redux-saga/effects';
import { push as routerReduxPush } from 'react-router-redux';
import { get } from 'lodash';
import { errorCodes } from 'config';

import request from 'utils/request';
import { generateTakeLatestSaga } from 'utils/sagas';


import {
	getCart,
	clearFavorites,
	setErrorMessage,
	setErrorModal
} from 'pages/App/actions';
import {
	clear,
	setCountries,
	setEmailPreference,
	setError,
	setLoading,
	setUserBilling,
	setUserCreditCard,
	setUserMailing,
	setUserProfile,
	saveUserBilling as saveUserBillingAction,
	saveUserMailing as saveUserMailingAction,
	saveUserProfile as saveUserProfileAction,
	saveUserPayment as saveUserPaymentAction,
	deleteUserCreditCard as deleteUserCreditCardAction,
	resetPassword as resetPasswordAction,
	setSelfHostedPageviews,
	setSelfHostedRenewalOverview,
	setSelfHostedUpgradeOverview,
	getSelfHostedUpgradeOverview as getSelfHostedUpgradeOverviewAction,
	getSelfHostedRenewalOverview as getSelfHostedRenewalOverviewAction,
	setAccountDeletionVerification,
	verifyAccountDeletion as verifyAccountDeletionAction,
	deleteAccount as deleteAccountAction
} from './actions';
import { setLoading as setCheckoutLoading } from 'pages/Checkout/actions';
import { selectCountries } from './selectors';
import { isAuthenticated, signOut } from 'utils/authentication';

import {
	CLEAR_USER,
	DELETE_USER_CREDIT_CARD,
	GET_COUNTRIES,
	GET_USER,
	RESET_PASSWORD,
	SAVE_EMAIL_PREFERENCE,
	SAVE_USER_BILLING,
	SAVE_USER_MAILING,
	SAVE_USER_PAYMENT,
	SAVE_USER_PROFILE,

	GET_SELF_HOSTED_PAGEVIEWS,
	GET_SELF_HOSTED_RENEWAL_OVERVIEW,
	GET_SELF_HOSTED_UPGRADE_OVERVIEW,

	VERIFY_ACCOUNT_DELETION,
	DELETE_ACCOUNT
} from './constants';
import {
	handleReauthentication
} from 'store/authentication/actions';

function getPaymentBillingBody(data) {
	return {
		bill_country: data.billing.country,
		bill_address1: data.billing.address1,
		bill_address2: data.billing.address2,
		bill_address3: data.billing.address3,
		bill_city: data.billing.city,
		bill_state: data.billing.state,
		bill_zip: data.billing.zip,

		bill_company: data.company,
		address_same: data.isMailingAddressSame ? '1' : '0',
		phone_no: data.phone,
		publication: data.publication,

		...((data.cardNumber ? {
			card_number: /[\d\s]+/.test(data.cardNumber) ? data.cardNumber.replace(/\s/g, '') : data.cardNumber,
			card_expiration_month: data.cardExpirationMonth,
			card_expiration_year: data.cardExpirationYear,
			card_code: data.cardCvv
		} : null))
	};
}

function getPaymentMailingBody(data) {
	return {
		ship_country: data.mailing.country,
		ship_address1: data.mailing.address1,
		ship_address2: data.mailing.address2,
		ship_address3: data.mailing.address3,
		ship_city: data.mailing.city,
		ship_state: data.mailing.state,
		ship_zip: data.mailing.zip
	};
}

export function* deleteUserCreditCard() {
	const type = 'deleteUserCreditCard';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		yield call(request, '/api/v1/user/payment_profile', { method: 'DELETE' });

		yield put(setUserCreditCard(false));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: deleteUserCreditCardAction,
			arguments: []
		}, setErrorMessage));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* clearUser() {
	const type = 'clearUser';

	try {
		yield put(clearFavorites());
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(setErrorMessage());
	}
}

export function* getUser() {
	const type = 'getUser';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		// All license types we need to check for
		const licenseTypes = [
			'app_subscription',
			'cloud_subscription',
			'computer_licenses',
			'self_hosted_licenses',
			'webfont_licenses'
		];

		const response = yield call(request, '/api/v1/user', {
			params: {
				include: licenseTypes.join(',')
			}
		});

		yield all([
			put(setUserProfile(response.data)),
			put(setUserBilling(response.data.data)),
			put(setUserMailing(response.data.data))
		]);
	} catch (error) {
		if (error.response.status === 401 && isAuthenticated()) {
			signOut();
			if (process.env.NODE_ENV === 'development' && process.env.LOG_ERRORS) {
				throw error;
			} else {
				Sentry.withScope(scope => {
					scope.setExtra('debug', false);
					Sentry.captureException(error);
				});
			}
			window.location.reload(false);
		} else {
			const message = get(error, 'response.data.error.message', error.message);
			yield put(setError({ type, error: message }));
			yield put(setErrorMessage());
		}
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* getCountries() {
	const type = 'getCountries';

	try {
		const currentCountries = yield select(selectCountries());
		if (currentCountries) {
			return;
		}

		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/countries');
		yield put(setCountries(response.data.data));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(setErrorMessage());
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* saveEmailPreference(action) {
	const type = 'saveEmailPreference';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(setEmailPreference(action.data.subscribe));
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const url = `/api/v1/user/${action.data.userId}/${action.data.subscribe ? 'subscribe' : 'unsubscribe'}`;
		yield call(request, url, { method: 'PUT' });
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(setEmailPreference(!action.data.subscribe));
		yield put(setErrorModal());
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* saveUserBilling(action) {
	const type = 'saveUserBilling';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const { data, options } = action;
		const response = yield call(request, '/api/v1/user', {
			method: 'PUT',
			includeEmpty: false,
			body: getPaymentBillingBody(data)
		});
		if (options && options.success) {
			yield call(options.success);
		}
		yield put(setUserBilling(response.data.data));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);

		yield put(setError({ type, error: message }));
		let errorCallback;
		let errorCallbackType;
		if (action.options && action.options.error) {
			errorCallback = action.options.error;
			errorCallbackType = 'call';
		}

		yield put(handleReauthentication(err, {
			action: saveUserBillingAction,
			arguments: [action.data, action.options]
		}, errorCallback, errorCallbackType));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* saveUserMailing(action) {
	const type = 'saveUserMailing';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/user', {
			method: 'PUT',
			body: getPaymentMailingBody(action.data)
		});

		yield put(setUserMailing(response.data.data));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: saveUserMailingAction,
			arguments: [action.data, action.options]
		}, setErrorMessage));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* saveUserPayment(action) {
	const type = 'saveUserPayment';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/user', {
			method: 'PUT',
			body: {
				...getPaymentBillingBody(action.data),
				...((action.data.isMailingAddressSame ? null : getPaymentMailingBody(action.data)))
			}
		});

		yield all([
			put(setUserBilling(response.data.data)),
			put(setUserMailing(response.data.data)),
			put(setUserProfile(response.data.data)),
			put(getCart())
		]);

		if (action.options && action.options.location) {
			yield put(routerReduxPush(action.options.location));
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		const errorCode = get(err, 'response.data.error.code');

		// If error is related to reauthentication…
		if (errorCode === errorCodes.REAUTHENTICATION) {
			yield put(handleReauthentication(err, {
				action: saveUserPaymentAction,
				arguments: [action.data, action.options]
			}, setErrorMessage));
		// Otherwise just show the error modal and display the error message
		} else {
			yield put(clear({ type }));
			yield put(setCheckoutLoading({ type, loading: false }));
			yield put(setErrorModal(`Error: ${message}`));
		}
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* saveUserProfile(action) {
	const type = 'saveUserProfile';
	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/user', {
			method: 'PUT',
			body: {
				full_name: action.data.name,
				website: action.data.website
			}
		});

		yield put(setUserProfile(response.data.data));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: saveUserProfileAction,
			arguments: [action.data, action.options]
		}));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* resetPassword(action) {
	const type = 'resetPassword';
	try {
		const { data, options } = action;
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		yield call(request, '/api/v1/user/reset_password', {
			method: 'PUT',
			body: data
		});
		if (options && options.success) {
			yield call(options.success);
		}
		yield put(routerReduxPush('/account/signout?redirect=signin'));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: resetPasswordAction,
			arguments: [action.data, action.options]
		}));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export const clearUserSaga = generateTakeLatestSaga(CLEAR_USER, clearUser);

export function* getSelfHostedPageviews() {
	const type = 'getSelfHostedPageviews';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/self_hosted_pageviews', {
			params: {
				serializer: 'array',
				show_as_upgrade: true
			}
		});
		yield put(setSelfHostedPageviews(response.data.self_hosted_pageviews));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(setErrorMessage());
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* getSelfHostedRenewalOverview(action) {
	const type = 'getSelfHostedRenewalOverview';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, '/api/v1/items', {
			params: {
				ids: [action.data.itemId],
				quantity: '0',
				self_hosted_pageview_id: action.data.selfHostedPageviewId,
				serializer: 'array'
			}
		});
		response.data.items[0].selfHostedPageviewId = action.data.selfHostedPageviewId;
		yield put(setSelfHostedRenewalOverview(response.data.items[0]));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: getSelfHostedRenewalOverviewAction,
			arguments: [action.data]
		}, setErrorMessage));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* getSelfHostedUpdateOverview(action) {
	const type = 'getSelfHostedUpdateOverview';

	try {
		// We send an action that tells Redux we're sending a request
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));

		const response = yield call(request, `/api/v1/self_hosted_license/${action.data.id}/upgrade_overview`, {
			params: {
				self_hosted_pageview_id: action.data.selfHostedPageviewId,
				serializer: 'array'
			}
		});
		response.data.selfHostedPageviewId = action.data.selfHostedPageviewId;
		yield put(setSelfHostedUpgradeOverview(response.data));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: getSelfHostedUpgradeOverviewAction,
			arguments: [action.data]
		}, setErrorMessage));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* verifyAccountDeletion(action) {
	const type = 'verifyAccountDeletion';
	const { options } = action;
	try {
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));
		const user = yield select(selectUserProfile());
		const response = yield call(request, `/api/v1/user/${user.id}/verify_deletion`);
		yield put(setAccountDeletionVerification(response.data.data));
		if (options && options.callback) {
			yield call(options.callback);
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: verifyAccountDeletionAction,
			arguments: [action.options]
		}, get(options, 'callback'), 'call'));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* deleteAccount(action) {
	const type = 'deleteAccount';
	const { options } = action;
	try {
		yield put(clear({ type }));
		yield put(setLoading({ type, loading: true }));
		const user = yield select(selectUserProfile());
		yield call(request, `/api/v1/user/${user.id}`, {
			method: 'DELETE'
		});
		yield put(routerReduxPush('/account/signout?redirect=home'));
		if (options && options.success) {
			yield call(options.success);
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield put(setError({ type, error: message }));
		yield put(handleReauthentication(err, {
			action: deleteAccountAction,
			arguments: [action.options]
		}));
		if (options && options.error) {
			yield call(options.error);
		}
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		if (options && options.callback) {
			yield call(options.callback);
		}
		yield put(setLoading({ type, loading: false }));
	}
}

export const getUserSaga = generateTakeLatestSaga(GET_USER, getUser);
export const getCountriesSaga = generateTakeLatestSaga(GET_COUNTRIES, getCountries);
export const deleteUserCreditCardSaga = generateTakeLatestSaga(DELETE_USER_CREDIT_CARD, deleteUserCreditCard);
export const saveEmailPreferenceSaga = generateTakeLatestSaga(SAVE_EMAIL_PREFERENCE, saveEmailPreference);
export const saveUserBillingSaga = generateTakeLatestSaga(SAVE_USER_BILLING, saveUserBilling);
export const saveUserMailingSaga = generateTakeLatestSaga(SAVE_USER_MAILING, saveUserMailing);
export const saveUserPaymentSaga = generateTakeLatestSaga(SAVE_USER_PAYMENT, saveUserPayment);
export const saveUserProfileSaga = generateTakeLatestSaga(SAVE_USER_PROFILE, saveUserProfile);
export const resetPasswordSaga = generateTakeLatestSaga(RESET_PASSWORD, resetPassword);
export const getSelfHostedPageviewsSaga = generateTakeLatestSaga(GET_SELF_HOSTED_PAGEVIEWS, getSelfHostedPageviews);
export const getSelfHostedRenewalOverviewSaga = generateTakeLatestSaga(GET_SELF_HOSTED_RENEWAL_OVERVIEW, getSelfHostedRenewalOverview);
export const getSelfHostedUpdateOverviewSaga = generateTakeLatestSaga(GET_SELF_HOSTED_UPGRADE_OVERVIEW, getSelfHostedUpdateOverview);
export const verifyAccountDeletionSaga = generateTakeLatestSaga(VERIFY_ACCOUNT_DELETION, verifyAccountDeletion);
export const deleteAccountSaga = generateTakeLatestSaga(DELETE_ACCOUNT, deleteAccount);


// All sagas to be loaded
export default [
	clearUserSaga,
	getCountriesSaga,
	getUserSaga,
	deleteUserCreditCardSaga,
	saveEmailPreferenceSaga,
	saveUserBillingSaga,
	saveUserMailingSaga,
	saveUserPaymentSaga,
	saveUserProfileSaga,
	resetPasswordSaga,
	getSelfHostedPageviewsSaga,
	getSelfHostedRenewalOverviewSaga,
	getSelfHostedUpdateOverviewSaga,
	verifyAccountDeletionSaga,
	deleteAccountSaga
];
